import { ReactNode } from 'react';
import type { InfoDrawerValue } from '../providers/InfoDrawer';
import type { IInfoDrawerValues } from './infoDrawerValues';
import styles from './pageGuides.module.scss';

/**
 * GUIDE DI PAGINA — l'help onnicomprensivo «Come funziona questa pagina».
 *
 * Registro centralizzato e DICHIARATIVO: ogni pagina si descrive con una `intro`
 * (a cosa serve, cosa mostra, come si legge) e l'elenco delle metriche che
 * contiene, per CHIAVE. Le spiegazioni delle metriche NON vengono riscritte qui:
 * `buildPageGuide` le compone pescandole dal registro unico delle guide
 * (`INFO_DRAWER_VALUES`), così esiste una sola fonte di verità — se cambia la
 * guida dell'ACWR, cambia anche dentro la guida di pagina.
 *
 * Aggiungere una pagina = una voce qui (intro + chiavi). Nient'altro.
 */

/** Chiavi delle metriche componibili (quelle già nel registro guide). */
type MetricKey = keyof IInfoDrawerValues;

export interface PageGuideDef {
  /** Titolo del drawer, es. «Come funziona: Readiness». */
  pageTitle: string;
  /** Introduzione della pagina: a cosa serve, cosa mostra, come si legge. */
  intro: ReactNode;
  /** Metriche/concetti della pagina, in ordine: comporre le loro guide sotto l'intro. */
  metricKeys: MetricKey[];
}

export const PAGE_GUIDES = {
  'page-readiness': {
    pageTitle: 'Come funziona: Readiness',
    intro: (
      <>
        <p className={styles.p}>
          La matrice Readiness mostra <strong>lo stato di recupero di tutta la
          squadra in un colpo</strong>, per il giorno scelto. Ogni riga è un atleta;
          la colonna principale è il <strong>Recovery Index (RI)</strong>, il numero
          0–100 che riassume quanto è recuperato, con accanto i dati grezzi che lo
          alimentano (sonno, stress, umore, CMJ, HRV, FC a riposo, sRPE, ACWR).
        </p>
        <p className={styles.p}>
          Serve a capire <strong>a chi guardare per primo</strong>: i RI bassi (rossi)
          sono gli atleti da approfondire o da alleggerire. Il colore del numero segue
          le soglie del RI; il badge accanto (PARZIALE / BASSA CONF.) dice su quanti
          dati poggia quel valore — non se è alto o basso.
        </p>
      </>
    ),
    metricKeys: ['ri', 'confidenceBadge', 'questionnaireCoverage'],
  },

  'page-effort': {
    pageTitle: 'Come funziona: Sforzo',
    intro: (
      <>
        <p className={styles.p}>
          La pagina Sforzo mostra il <strong>carico interno della squadra</strong> nel
          tempo: quanto è pesato l&apos;allenamento per gli atleti, non i minuti in
          campo. La heatmap colora ogni giorno per fascia di carico (sRPE); accanto
          trovi l&apos;<strong>ACWR</strong> (stai aumentando troppo in fretta?) e la
          <strong> monotonia</strong> (i giorni sono tutti uguali?).
        </p>
        <p className={styles.p}>
          Serve a <strong>dosare il carico</strong>: leggere insieme volume (sRPE),
          progressione (ACWR) e varietà (monotonia) dice se la settimana è costruita
          bene o se sta accumulando rischio.
        </p>
      </>
    ),
    metricKeys: ['srpe', 'acwr', 'monotony'],
  },

  'page-afi': {
    pageTitle: 'Come funziona: Antifragilità',
    intro: (
      <>
        <p className={styles.p}>
          La pagina Antifragilità (AFI) misura la <strong>robustezza strutturale</strong>
          dell&apos;atleta nel tempo, non lo stato di oggi (quello è il Recovery Index).
          Risponde a: «quanto è preparato ad affrontare un imprevisto?». È uno score
          0–100 calcolato ogni settimana su tre componenti (stabilità fisiologica,
          capacità di recovery, margine strutturale).
        </p>
        <p className={styles.p}>
          Serve alla <strong>lettura di lungo periodo</strong>: il trend conta più del
          valore assoluto, e il pattern delle tre componenti dice dove l&apos;atleta è
          vulnerabile quando arriva lo shock.
        </p>
      </>
    ),
    metricKeys: ['afi'],
  },

  'page-recovery': {
    pageTitle: 'Come funziona: Recupero',
    intro: (
      <>
        <p className={styles.p}>
          La pagina Recupero mostra il <strong>Recovery Index della squadra</strong> e
          il suo andamento: combina le componenti mentale, muscolare, cardio e strain
          in un unico numero 0–100 per atleta (la sezione qui sotto le spiega).
        </p>
        <p className={styles.p}>
          Serve a <strong>vedere chi non ha recuperato</strong> prima di programmare la
          seduta: sotto le soglie di attenzione, valuta di alleggerire il carico o di
          aprire il dettaglio atleta.
        </p>
      </>
    ),
    metricKeys: ['ri'],
  },

  'page-combined': {
    pageTitle: 'Come funziona: Sforzo + Recovery',
    intro: (
      <>
        <p className={styles.p}>
          Questa vista <strong>incrocia carico e recupero</strong> nella stessa
          settimana: ogni cella mostra insieme il carico interno (sRPE, in alto) e il
          Recovery Index (RI, in basso), per vedere a colpo d&apos;occhio se dove hai
          spinto l&apos;atleta ha poi recuperato.
        </p>
        <p className={styles.p}>
          Serve a <strong>leggere causa ed effetto</strong>: carico alto seguito da RI
          basso è il segnale da non ignorare. Sotto trovi la spiegazione di tutte le
          misure in gioco, sia di sforzo sia di recupero.
        </p>
      </>
    ),
    metricKeys: ['srpe', 'acwr', 'monotony', 'ri'],
  },
} satisfies Record<string, PageGuideDef>;

export type IPageGuideKey = keyof typeof PAGE_GUIDES;

/**
 * Guardia statica: una page-key NON deve collidere con una metric-key, altrimenti
 * nel provider la guida di pagina ombreggerebbe silenziosamente la metrica. Il
 * prefisso 'page-' lo garantisce; questo tipo lo impone a compile-time (se una
 * chiave collide, il tipo diventa un messaggio d'errore e la riga sotto non compila).
 */
type _NoKeyCollision = Extract<IPageGuideKey, MetricKey> extends never
  ? true
  : 'COLLISIONE page-key / metric-key: rinomina la page-key';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _assertNoCollision: _NoKeyCollision = true;

/** True se la chiave è una guida di pagina (vs una metrica singola). */
export const isPageGuideKey = (key: string): key is IPageGuideKey =>
  key in PAGE_GUIDES;

/**
 * Compone la guida di pagina in un `InfoDrawerValue`: intro + una sezione per
 * ogni metrica, RIUSANDO title e description dal registro guide passato. Riceve
 * il registro come parametro (non lo importa) per evitare cicli di import.
 */
export function buildPageGuide(
  def: PageGuideDef,
  registry: IInfoDrawerValues,
): InfoDrawerValue {
  return {
    title: def.pageTitle,
    placement: 'end',
    size: 'lg',
    description: (
      <div className={styles.page}>
        <div className={styles.intro}>{def.intro}</div>
        {def.metricKeys.map((k) => {
          const g = registry[k];
          if (!g) return null;
          return (
            <section key={String(k)} className={styles.section}>
              <div className={styles.sectionHead}>{g.title}</div>
              {g.description}
            </section>
          );
        })}
      </div>
    ),
  };
}
